import { Box, Grid, Stack, SvgIcon, Typography, useMediaQuery, Divider } from '@mui/material'
import { useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useState, useCallback } from 'react'

import cameraIcon from '@hapana/shared/web/styles/assets/svgs/camera.svg'
import note from '@hapana/shared/web/styles/assets/svgs/note.svg'
import packages from '@hapana/shared/web/styles/assets/svgs/package-small.svg'
import pinLocation from '@hapana/shared/web/styles/assets/svgs/map-marker-alt-small.svg'
import schedule from '@hapana/shared/web/styles/assets/svgs/schedule-small.svg'
import shoppingCart from '@hapana/shared/web/styles/assets/svgs/shopping-cart-small.svg'
import { ALERT_TYPES } from '@hapana/shared/web/components/shared/alerts/alert.constant'
import {
  AVATAR_SIZE,
  AVATAR_TYPES,
  AVATAR_VARIANT,
} from '@hapana/shared/web/components/shared/avatar/avatar.constants'
import Avatar from '@hapana/shared/web/components/shared/avatar/single-avatar.component'
import BannerAlert from '@hapana/shared/web/components/shared/banner-alert/banner-alert.component'
import {
  FAILED_PAYMENT_REASONS,
  SEVERITY,
  SIZE,
} from '@hapana/shared/web/components/shared/banner-alert/banner-alert.constants'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_SIZE,
  BUTTON_THEME,
  BUTTON_VARIANTS,
} from '@hapana/shared/web/components/shared/button/button.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { PAYMENT_TYPE } from '@hapana/shared/web/components/shared/individual-alerts/individual-alerts.constants'
import Skeleton from '@hapana/shared/web/components/shared/skeleton/skeleton.component'
import { SKELETON_VARIANTS } from '@hapana/shared/web/components/shared/skeleton/skeleton.constants'
import Tooltip from '@hapana/shared/web/components/shared/tooltip/tooltip.component'
import { TOOLTIP_POSITION } from '@hapana/shared/web/components/shared/tooltip/tooltip.constants'
import { TIME_FORMATS } from '@hapana/shared/constants/date.constants'
import { useSiteNames } from '@hapana/shared/web/hooks/site.hooks'
import useSiteStore from '_stores/site.store'
import {
  useSiteConfiguration,
  useTimeFormat,
} from '@hapana/shared/web/hooks/siteConfiguration.hooks'
import { useSiteEnableTanningFeatures } from '_hooks/sites.hooks'
import { ROUTES } from '../../../app.constants'
import Badges from '@components/check-in-widget/badges/badges.component'
import AddNoteDialog from '@pages/clients/components/client-notes/dialogs/add-note.component'
import ModalAlerts from '@components/check-in-widget/modal-alerts/modal-alerts.component'
import { TITLE as CHECK_IN_TITLE } from '@components/check-in-widget/modal-check-in/modal-check-in.constants'
import ModalUploadPhoto from '@hapana/shared/web/components/shared/modal-upload-photo/modal-upload-photo.component'
import ViewAllAlerts from '@components/check-in-widget/view-alerts/view-alerts.component'
import { EquipmentsAvailable } from '@components/check-in-widget/most-recent-card/equipments-available/equipments-available.component'
import { useClientActivePackages } from '@hapana/shared/web/hooks/client.hooks'
import { CHECK_IN_STATUSES } from '@hapana/shared/constants/packages.constants'
import { resolved, mutation as gqtyMutation } from '@hapana/shared/web/services/gqty'
import { uploadToSignedUrl } from '@hapana/shared/web/services/upload.service'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { useMe } from '_hooks/me.hooks'

import PaymentsList from '../payments-list/payments-list.component'

import { styles } from './most-recent-card.styles'
import { isFirstSession } from './most-recent-card.util'

const MostRecentCard = ({
  profileImg,
  name,
  firstName,
  status,
  date,
  checkInTime,
  membership,
  location,
  nextSession,
  payments,
  clientId,
  failReason,
  birthday,
  homeSites,
  totalCheckInCount,
  siteInformation,
  siteNotes,
  siteAlertedProperties,
  formsPending,
  sitesInIntroOffer,
  checkInId,
}) => {
  const { t } = useTranslation(['checkIn', 'core', 'common'])
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { siteId: paramSiteId } = useParams()
  const { pushDialog } = useDialog()
  const { openToast } = useToast()
  const { data: meData } = useMe()
  const resolvedSiteId = paramSiteId || siteId
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const profileUrl = resolvedSiteId && clientId ? `/s/${resolvedSiteId}/clients/${clientId}` : null
  const saleUrl =
    resolvedSiteId && clientId
      ? `/s/${resolvedSiteId}/${ROUTES.retailDashboard.children.newSale.path}?clientId=${clientId}`
      : resolvedSiteId
        ? `/s/${resolvedSiteId}/${ROUTES.retailDashboard.children.newSale.path}`
        : null
  const { data: configuration } = useSiteConfiguration()
  const { data: siteEnableTanningFeatures } = useSiteEnableTanningFeatures()

  const { data: packagesData } = useClientActivePackages(clientId)

  const handleImageUpload = useCallback(
    (data, callbacks) => {
      const userId = meData?.me?.id
      if (!userId) {
        openToast({ title: t('common:somethingWentWrong'), severity: 'error' })
        return
      }
      setIsUploadingImage(true)
      resolved(
        () => {
          const result = gqtyMutation.updateClientProfileImage({
            clientId,
            input: { format: data.fileExtension },
          })
          return {
            tempUrl: result.tempUrl,
            requestedBy: result.requestedBy,
            userType: result.userType,
          }
        },
        { noCache: true }
      )
        .then((response) => uploadToSignedUrl(response, data.file))
        .then((uploadResponse) => {
          if (!uploadResponse.ok) {
            throw new Error('Upload failed')
          }
          setIsUploadingImage(false)
          openToast({ title: t('core:userProfile.profileImageProcessing'), severity: 'info' })
          callbacks?.onSuccess?.()
        })
        .catch((error) => {
          setIsUploadingImage(false)
          openToast({ title: t('common:somethingWentWrong'), severity: 'error' })
          callbacks?.onError?.(error)
        })
    },
    [clientId, meData?.me?.id, openToast, t]
  )

  const clientImageMutation = {
    mutate: handleImageUpload,
    isLoading: isUploadingImage,
    responseKey: 'updateClientProfileImage',
  }

  const { data: homeLocations, isIdle: isHomeLocation } = useSiteNames(homeSites)
  const homeLocationNames = homeLocations?.map((homeLocation) => homeLocation.name)

  const { data: timeFormatData } = useTimeFormat()

  const matchesDesktop = useMediaQuery((theme) => theme.breakpoints.up('sm'))
  const hasTanningFeatures = siteEnableTanningFeatures?.adminConfigurations?.enableTanningFeatures

  const filteredFormsPending = formsPending
    ?.filter((form) => form.siteId === siteId)
    .map((form) => form.title)

  const isStatusUnknown = status?.toUpperCase() === CHECK_IN_STATUSES.unknown

  const handleBannerOptions = () => {
    if (status?.toUpperCase() === SEVERITY.SUCCESS.toUpperCase()) {
      if (payments?.failed?.length > 0 /* || payments?.outstanding?.length > 0 */) {
        return {
          severity: SEVERITY.WARNING,
          message: t('checkIn:status.valid'),
        }
      }
      return {
        severity: SEVERITY.SUCCESS,
        message: t('checkIn:status.valid'),
      }
    }
    return {
      severity: SEVERITY.ERROR,
      message: t('checkIn:status.invalid'),
    }
  }

  const bannerOptions = {
    ...handleBannerOptions(),
    description:
      payments?.failed?.length > 0 /* || payments?.outstanding?.length > 0 */
        ? `${t('withAlerts')}`
        : '',
  }
  const locationWidth = location?.length > 12 ? 12 : 'auto'
  const sessionWidth = nextSession?.title?.length > 10 ? 12 : 'auto'

  const currentTimezone = configuration?.timezone

  const hasNoClientRecord =
    status?.toUpperCase() === CHECK_IN_STATUSES.fail &&
    failReason?.toUpperCase() ===
      FAILED_PAYMENT_REASONS.NO_VALID_MEMBERSHIP_OR_BOOKING_AND_NO_CLIENT_RECORD.key

  const hasNotesAndProperties =
    siteNotes?.find((s) => s.siteId === siteId) ||
    siteAlertedProperties?.find((s) => s.siteId === siteId)

  const introOffer = sitesInIntroOffer?.find((s) => s === siteId)
  const isSuccess = status?.toUpperCase() === SEVERITY.SUCCESS.toUpperCase()

  const isFirstSessionInLocation = isFirstSession({
    siteId,
    checkInDate: checkInTime,
    siteInformation,
    isSuccess,
  })
  const getAlerts = () => {
    const alerts = []

    if (payments?.failed?.length > 0 /* || payments?.outstanding?.length > 0 */) {
      alerts.push(ALERT_TYPES.PAYMENT)
    }
    filteredFormsPending?.length > 0 && alerts.push(ALERT_TYPES.FORM)
    if (hasNotesAndProperties) {
      alerts.push(ALERT_TYPES.NOTE)
    }
    !isHomeLocation && alerts.push(ALERT_TYPES.HOME)

    birthday && alerts.push(ALERT_TYPES.BIRTHDAY)
    packagesData?.clientSiteActivePackages?.length > 0 && alerts.push(ALERT_TYPES.PACKAGE)
    isFirstSessionInLocation && alerts.push(ALERT_TYPES.FIRST_SESSION)
    introOffer && alerts.push(ALERT_TYPES.INTRO_OFFER)

    if (alerts?.length > 0) {
      return alerts
    } else {
      return false
    }
  }

  return (
    <Grid sx={styles.dialogContent}>
      <Grid item xs={12}>
        <BannerAlert
          message={bannerOptions.message}
          description={bannerOptions.description}
          extraInfo={date}
          failReason={failReason}
          severity={bannerOptions.severity}
          size={SIZE.SMALL}
          hasBackground
          themedMessage
          hasExtraDivider
          hasFailReasonDivider
          isLoading={isStatusUnknown}
        />
      </Grid>
      <Box sx={[styles.mostRecentCheckInContainer, styles.contentContainer, styles.gridTemplate]}>
        <Box sx={styles.avatarHolder}>
          <Tooltip placement={TOOLTIP_POSITION.bottom} title={t('checkIn:changeImage')}>
            <Box
              sx={styles.avatarOverlay}
              onClick={() =>
                !hasNoClientRecord &&
                pushDialog(
                  ModalUploadPhoto,
                  { mutation: clientImageMutation },
                  {
                    dialogFilterParams: { key: 'title', value: CHECK_IN_TITLE },
                  }
                )
              }
            >
              <SvgIcon sx={styles.avatarIcon} component={cameraIcon} viewBox="0 0 16 17" />
              <Avatar
                variant={AVATAR_VARIANT.CIRCULAR}
                avatarType={AVATAR_TYPES.SINGLE_AVATAR}
                item={{
                  alt: 'Avatar image',
                }}
                src={profileImg?.url}
                size={matchesDesktop ? AVATAR_SIZE.PROFILE : AVATAR_SIZE.EXTRA_LARGE}
              />
            </Box>
          </Tooltip>
        </Box>

        <Box>
          <Typography typography="header3" color="gray.100" sx={styles.userName}>
            {name}
          </Typography>
          <Button variant="text" textPadding={false} href={profileUrl} disabled={hasNoClientRecord}>
            {t('checkIn:viewProfile')}
          </Button>
        </Box>
        <Stack spacing={{ xs: 2, md: 3 }} sx={[styles.infoContainer, styles.addNoteSpacing]}>
          <Grid container rowSpacing={{ xs: 1, sm: 1.5 }} columnSpacing={3}>
            <Grid item xs={12} sx={styles.locationSession}>
              {isStatusUnknown ? (
                <>
                  <Skeleton
                    variant={SKELETON_VARIANTS.circular}
                    sx={styles.smallCircularSkeleton}
                  />
                  <Skeleton
                    isRounded
                    variant={SKELETON_VARIANTS.rectangular}
                    sx={styles.rectangularSkeleton}
                  />
                </>
              ) : (
                <>
                  <SvgIcon
                    component={packages}
                    sx={membership ? styles.packagesIcon : styles.noPackagesIcon}
                  />
                  <Typography typography="medium" color={membership ? 'gray.64' : 'gray.40'}>
                    {membership ? membership.title : t('checkIn:noMembership')}
                  </Typography>
                </>
              )}
            </Grid>
            <Grid
              container
              item
              xs={12}
              sm={locationWidth}
              wrap="nowrap"
              sx={styles.locationSession}
            >
              {isStatusUnknown ? (
                <>
                  <Skeleton
                    variant={SKELETON_VARIANTS.circular}
                    sx={styles.smallCircularSkeleton}
                  />
                  <Skeleton
                    isRounded
                    variant={SKELETON_VARIANTS.rectangular}
                    sx={styles.rectangularSkeleton}
                  />
                </>
              ) : (
                <>
                  <SvgIcon
                    component={pinLocation}
                    sx={location ? styles.packagesIcon : styles.noPackagesIcon}
                  />
                  <Typography typography="medium" color={location ? 'gray.64' : 'gray.40'}>
                    {location ? location : t('checkIn:noLocation')}
                  </Typography>
                </>
              )}
            </Grid>
            <Grid
              container
              item
              xs={12}
              sm={sessionWidth}
              wrap="nowrap"
              sx={styles.locationSession}
            >
              {isStatusUnknown ? (
                <>
                  <Skeleton
                    variant={SKELETON_VARIANTS.circular}
                    sx={styles.smallCircularSkeleton}
                  />
                  <Skeleton
                    isRounded
                    variant={SKELETON_VARIANTS.rectangular}
                    sx={styles.rectangularSkeleton}
                  />
                </>
              ) : (
                <>
                  <SvgIcon
                    component={schedule}
                    sx={nextSession ? styles.packagesIcon : styles.noPackagesIcon}
                  />
                  <Box>
                    {nextSession ? (
                      <>
                        <Typography component="span" sx={styles.membershipField}>
                          {nextSession.title}
                        </Typography>
                        <Typography component="span" sx={styles.circle}>
                          {' • '}
                        </Typography>
                        <Typography component="span" sx={styles.membershipField}>
                          {format(
                            utcToZonedTime(new Date(nextSession.datetime), currentTimezone),
                            `${
                              timeFormatData?.timeFormat === TIME_FORMATS.T12.value
                                ? TIME_FORMATS.T12.pattern
                                : TIME_FORMATS.T24.pattern
                            }:mm aaa`
                          )}
                        </Typography>
                      </>
                    ) : (
                      <Typography component="span" typography="medium" color="gray.40">
                        {t('checkIn:noSessions')}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
          <Grid container sx={styles.profileOptions}>
            <Button
              variant={BUTTON_VARIANTS.OUTLINED}
              size={BUTTON_SIZE.SMALL}
              theme={BUTTON_THEME.GRAY}
              startIcon={note}
              disabled={hasNoClientRecord}
              onClick={() =>
                pushDialog(AddNoteDialog, {
                  client: { id: clientId, name, firstName },
                })
              }
              sx={[styles.cartAndAddNoteButtons, styles.addNoteButton]}
            >
              {t('checkIn:note.addNote')}
            </Button>
            <Button
              variant={BUTTON_VARIANTS.ICON}
              size={BUTTON_SIZE.SMALL}
              icon={shoppingCart}
              stroke
              href={saleUrl}
              disabled={hasNoClientRecord}
              sx={[styles.cartAndAddNoteButtons, styles.cartButton]}
            />
            <Box sx={styles.badgesWrapper}>
              {isStatusUnknown ? (
                <Box sx={styles.flexRowCentered}>
                  <Skeleton
                    variant={SKELETON_VARIANTS.circular}
                    sx={styles.mediumCircularSkeleton}
                  />
                  <Skeleton
                    variant={SKELETON_VARIANTS.circular}
                    sx={{
                      ...styles.mediumCircularSkeleton,
                      ...styles.badgeSkeletonSpacing,
                    }}
                  />
                </Box>
              ) : (
                <Badges
                  checkInDate={checkInTime}
                  totalCheckInCount={totalCheckInCount}
                  siteInformation={siteInformation}
                  isSuccess={isSuccess}
                />
              )}
            </Box>
          </Grid>
        </Stack>
      </Box>

      {isStatusUnknown ? (
        <>
          <Box sx={styles.alertsSkeletonContainer}>
            <Skeleton variant={SKELETON_VARIANTS.circular} sx={{ height: 20, width: 20 }} />
            <Skeleton isRounded sx={{ marginLeft: 1 }} variant={SKELETON_VARIANTS.rectangular} />
          </Box>
          <Box sx={styles.alertsSkeletonContainer}>
            <Box sx={styles.skeletonGroup}>
              <Skeleton variant={SKELETON_VARIANTS.circular} sx={styles.mediumCircularSkeleton} />
              <Skeleton variant={SKELETON_VARIANTS.circular} sx={styles.skeletonGroupItem} />
              <Skeleton variant={SKELETON_VARIANTS.circular} sx={styles.skeletonSecondGroupItem} />
            </Box>

            <Skeleton isRounded sx={{ marginLeft: 3.5 }} variant={SKELETON_VARIANTS.rectangular} />
          </Box>
        </>
      ) : (
        !hasNoClientRecord && (
          <Stack divider={<Divider flexItem />} sx={styles.alertsWrapper}>
            {payments?.failed?.length > 0 && (
              <PaymentsList type={PAYMENT_TYPE.FAILED} payments={payments?.failed} />
            )}
            {/* {payments?.outstanding?.length > 0 && (
              <PaymentsList type={PAYMENT_TYPE.OUTSTANDING} payments={payments?.outstanding} />
            )} */}
            {getAlerts() && (
              <Grid item sx={styles.viewAlertsWrapper}>
                <ViewAllAlerts
                  hasTextLink
                  alerts={getAlerts()}
                  onClick={() =>
                    pushDialog(ModalAlerts, {
                      siteId: resolvedSiteId,
                      profileImg,
                      name,
                      firstName,
                      failedPayments: payments?.failed,
                      // outstandingPayments: payments?.outstanding,
                      clientId,
                      birthday,
                      hasNotesAndProperties,
                      formsPending: filteredFormsPending,
                      isFirstSession: isFirstSessionInLocation,
                      introOffer,
                      location,
                      ...(!isHomeLocation && {
                        homeLocations: homeLocationNames,
                      }),
                    })
                  }
                />
              </Grid>
            )}
          </Stack>
        )
      )}
      {hasTanningFeatures && <EquipmentsAvailable clientId={clientId} clientName={name} />}
    </Grid>
  )
}

MostRecentCard.propTypes = {
  clientId: PropTypes.string.isRequired,
  profileImg: PropTypes.shape({
    url: PropTypes.string,
  }).isRequired,
  name: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  membership: PropTypes.shape({
    title: PropTypes.string,
  }),
  location: PropTypes.string,
  nextSession: PropTypes.shape({
    title: PropTypes.string,
    datetime: PropTypes.string,
  }),
  checkInTime: PropTypes.string,
  payments: PropTypes.shape({
    outstanding: PropTypes.array,
    failed: PropTypes.array,
  }),
  failReason: PropTypes.string,
  birthday: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  formsPending: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      siteId: PropTypes.string,
    })
  ),
  homeSites: PropTypes.arrayOf(PropTypes.string),
  totalCheckInCount: PropTypes.number,
  siteInformation: PropTypes.arrayOf(
    PropTypes.shape({
      siteId: PropTypes.string,
      lastCheckInOn: PropTypes.string,
      checkInCount: PropTypes.number,
    })
  ),
  siteNotes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      siteId: PropTypes.string,
    })
  ),
  siteAlertedProperties: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      siteId: PropTypes.string,
    })
  ),

  sitesInIntroOffer: PropTypes.array,
  checkInId: PropTypes.string,
}

MostRecentCard.default = {
  totalCheckInCount: 0,
}

MostRecentCard.defaultProps = {
  siteNotes: [],
  siteAlertedProperties: [],
}

export default MostRecentCard
